import { parseFrontMatter } from './frontMatter'
import { renderGuideMarkdown } from './markdown'
import type { TocEntry } from './markdown'
import type { GuideMeta } from './frontMatter'

export interface Guide extends GuideMeta {
  html: string
  toc: TocEntry[]
}

/**
 * Bundles `docs/guides/*.md` into the SPA at build time. That directory sits
 * at the repo root, outside `docker/admin-panel/`'s own Docker build context
 * (see the Dockerfile's own header comment), so this glob does not read
 * `docs/guides` directly -- it reads `guidesContent/`, a staged copy this
 * package's `.gitignore` deliberately excludes.
 * `scripts/stage-admin-panel-guides.sh` is the ONE place that copy is made,
 * run before `npm run dev`/`build`/`test` (via the `pre*` hooks below) and
 * again, into the Docker build context, by `build-mcp.sh` for this service.
 * docs/guides itself is the source of truth; nothing here ever writes to it.
 *
 * An empty `guidesContent/` (a fresh checkout, before the staging script has
 * run once) is not an error: the glob returns nothing and `GUIDES` is `[]`,
 * which the reader already renders as "No guides yet" rather than crashing.
 */
const RAW_MODULES = import.meta.glob('../guidesContent/*.md', {
  eager: true,
  query: '?raw',
  import: 'default',
}) as Record<string, string>

function slugFromPath(path: string): string {
  const file = path.split('/').pop() ?? path
  return file.replace(/\.md$/, '')
}

/**
 * Portal-first framing (Round 14, Unit 11,
 * `docs/design/specs/2026-09-24-policy-acceptance-and-api-users.md`): the
 * two guides the docs teammate added on 24 Sep -- About CHU-GPT and the AI
 * usage policy -- lead the list, ahead of everything else.
 *
 * NOT achieved by sorting on title text, which was tried first and does not
 * work: "A prompt guide for every job at CHU" sorts BEFORE "About CHU-GPT"
 * on a punctuation accident (a space's code point sorts below a letter's),
 * and the policy guide's actual title is "CHU AI usage policy", which lands
 * third, split away from About CHU-GPT by "Building dashboards with
 * CHU-GPT". Ordering on SLUG instead is stable against both problems, and
 * against the docs teammate wording either title however they like in the
 * future -- their content is theirs to word; only this list's ORDER is a
 * presentation decision on this side.
 */
const PRIORITY_SLUGS = ['about', 'policy']

export const GUIDES: Guide[] = Object.entries(RAW_MODULES)
  .map(([path, raw]) => {
    const parsed = parseFrontMatter(raw, slugFromPath(path))
    const { html, toc } = renderGuideMarkdown(parsed.body)
    const guide: Guide = { title: parsed.title, slug: parsed.slug, html, toc }
    if (parsed.audience) guide.audience = parsed.audience
    if (parsed.updated) guide.updated = parsed.updated
    return guide
  })
  .sort((a, b) => {
    const ai = PRIORITY_SLUGS.indexOf(a.slug)
    const bi = PRIORITY_SLUGS.indexOf(b.slug)
    if (ai !== -1 || bi !== -1) {
      // Both priority: keep PRIORITY_SLUGS' own order (About before Policy).
      // One priority: it always wins. Neither: fall through to title order.
      if (ai !== -1 && bi !== -1) return ai - bi
      return ai !== -1 ? -1 : 1
    }
    return a.title.localeCompare(b.title)
  })

export function findGuide(slug: string | null): Guide | null {
  if (!slug) return null
  return GUIDES.find((g) => g.slug === slug) ?? null
}

/** Guides whose title or any heading matches `query` (case-insensitive
 *  substring). Empty query matches everything -- the index is the full list
 *  until someone types. */
export function searchGuides(query: string): Guide[] {
  const q = query.trim().toLowerCase()
  if (!q) return GUIDES
  return GUIDES.filter(
    (g) => g.title.toLowerCase().includes(q) || g.toc.some((t) => t.text.toLowerCase().includes(q)),
  )
}
